/**
 * Server-rendered per-page head tags so link previews (iMessage, WhatsApp,
 * LinkedIn, X) show each page's own title, description and address.
 * Client-side useSEO still runs and keeps tags in sync after navigation.
 */
export const SITE_URL = "https://getamorette.com";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

type HeadInput = {
  path: string;
  title: string;
  description: string;
  image?: string | null | undefined;
  type?: "website" | "article";
  /** Omit canonical when another script owns it (e.g. Overrank on /insights). */
  canonical?: boolean;
};

export function pageHead({ path, title, description, image, type = "website", canonical = true }: HeadInput) {
  const url = `${SITE_URL}${path === "/" ? "/" : path}`;
  const img = image && /^https:\/\//.test(image) ? image : DEFAULT_IMAGE;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: type },
      { property: "og:url", content: url },
      { property: "og:image", content: img },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: img },
    ],
    links: canonical ? [{ rel: "canonical", href: url }] : [],
  };
}

const SUPABASE_URL = "https://mfqitbadznqekupsqbxv.supabase.co";
const ANON_KEY =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im1mcWl0YmFkem5xZWt1cHNxYnh2Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgzNTc3NDYsImV4cCI6MjA4MzkzMzc0Nn0.ncC5kKGWwF7JT4lz6SpSp9IiDXHsoY7FDKHSCJa9Iv4";

/** Public read (RLS applies as anon). Never throws — previews fall back to defaults. */
export async function publicRow<T>(query: string): Promise<T | null> {
  try {
    const res = await fetch(`${SUPABASE_URL}/rest/v1/${query}&limit=1`, {
      headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
    });
    if (!res.ok) return null;
    const rows = (await res.json()) as T[];
    return rows[0] ?? null;
  } catch {
    return null;
  }
}
